/**
 * scroll-target — pure math for the offset that reveals a target extent inside a
 * scroller/viewport along one axis. DOM-free; the caller supplies current offset,
 * the target's position RELATIVE to the scroll content, and the viewport length.
 *
 * Vertical bottom-image case: we want the target's END (bottom) to sit at the
 * viewport END (with a margin), i.e. scroll just enough to bring the whole element
 * into view, preferring to align the bottom when the element is below. When the
 * element is taller than the viewport, align its START (top) instead.
 *
 * @see src-js/model-finder/view/scroll-in-view.ts (the DOM caller)
 * @see docs/speed-optimization/research/2026-06-07 - model-finder-column-plan-and-modal/research/dropdown-bottom-image-autoscroll.md §3.2
 */

export interface RevealInput {
  /** Current scroll offset of the scroller (scrollTop|scrollLeft, or window.scrollY|X). */
  current: number;
  /** Target START relative to the scroll content (target rect start - viewport start + current). */
  targetStart: number;
  /** Target length (height|width) in px. */
  targetLength: number;
  /** Viewport length (clientHeight|clientWidth, or innerHeight|innerWidth) in px. */
  viewportLength: number;
  /** Max scroll offset; pass Infinity for the window (clamped by the browser anyway). */
  maxScroll: number;
  /** Desired margin (px) between the revealed edge and the viewport edge. */
  margin?: number;
  /**
   * Alignment policy. Default `'reveal'` = minimal scroll that brings the target
   * fully into view (bottom-align below the fold, top-align above / when taller).
   * `'center'` = park the target's centre at the viewport centre (clamped), i.e.
   * `scrollIntoView({block:'center'})` semantics — a mid-list node is centred, a
   * node near an end clamps to that end (margin is irrelevant and ignored).
   */
  align?: 'reveal' | 'center';
}

/**
 * The target scroll offset that brings `[targetStart, targetStart+targetLength]`
 * into view per `align`. For `'reveal'` (default): if the element is below the
 * fold, align its bottom to the viewport bottom minus margin; if above, align its
 * top to the viewport top plus margin; if taller than the viewport, align its top.
 * For `'center'`: place the target centre at the viewport centre. Clamped to
 * [0, maxScroll] either way.
 */
export function scrollOffsetToReveal(i: RevealInput): number {
  const margin = i.margin ?? 0;

  if (i.align === 'center') {
    // Target centre at viewport centre; clamp parks an end-of-list node flush at
    // that end (a bottom node → maxScroll, a top node → 0).
    const next = i.targetStart + i.targetLength / 2 - i.viewportLength / 2;
    return Math.round(Math.min(Math.max(next, 0), i.maxScroll));
  }

  const targetEnd = i.targetStart + i.targetLength;
  const viewStart = i.current;
  const viewEnd = i.current + i.viewportLength;

  let next = i.current;
  if (i.targetLength > i.viewportLength) {
    next = i.targetStart - margin; // taller than viewport → align top
  } else if (targetEnd > viewEnd) {
    next = targetEnd - i.viewportLength + margin; // below the fold → align bottom
  } else if (i.targetStart < viewStart) {
    next = i.targetStart - margin; // above the fold → align top
  }
  return Math.round(Math.min(Math.max(next, 0), i.maxScroll));
}
